/**
 * Sensitive Data Filtering Types and Interfaces
 * 
 * Comprehensive type definitions for the Universal Logger sensitive data filtering system.
 */

/**
 * Severity levels for filter rules
 */
export enum FilterSeverity {
  LOW = 'low',
  MEDIUM = 'medium', 
  HIGH = 'high',
  CRITICAL = 'critical'
}

/**
 * Categories of sensitive data
 */
export enum SensitiveDataCategory {
  PII = 'pii',
  AUTHENTICATION = 'authentication',
  FINANCIAL = 'financial',
  MEDICAL = 'medical',
  CUSTOM = 'custom'
}

/**
 * Filter rule types
 */
export enum FilterRuleType {
  REGEX = 'regex',
  KEYWORD = 'keyword',
  PATTERN = 'pattern',
  FUNCTION = 'function'
}

/**
 * Filter actions to take when sensitive data is detected
 */
export enum FilterAction {
  REDACT = 'redact',           // Replace with [REDACTED]
  MASK = 'mask',               // Replace with asterisks (keeping format)
  REMOVE = 'remove',           // Remove the field entirely
  HASH = 'hash',               // Replace with hash value
  TOKENIZE = 'tokenize'        // Replace with token
}

/**
 * Filter rule priority levels
 */
export enum FilterPriority {
  LOWEST = 0,
  LOW = 1,
  MEDIUM = 2,
  HIGH = 3,
  HIGHEST = 4
}

/**
 * Individual filter rule definition
 */
export interface FilterRule {
  id: string;
  name: string;
  description: string;
  category: SensitiveDataCategory;
  type: FilterRuleType;
  pattern: string | RegExp;
  action: FilterAction;
  priority: FilterPriority;
  severity: FilterSeverity;
  enabled: boolean;
  tags: string[];
  
  // Optional properties
  replacement?: string;        // Custom replacement text
  preserveFormat?: boolean;    // Keep original format when masking
  caseSensitive?: boolean;     // Case sensitivity for matching
  wholeWordOnly?: boolean;     // Match whole words only
  
  // Validation settings
  minLength?: number;          // Minimum match length
  maxLength?: number;          // Maximum match length
  
  // Performance settings
  timeout?: number;            // Max execution time in ms
  cacheResults?: boolean;      // Cache match results
  
  // Context restrictions
  fieldNames?: string[];       // Only apply to specific field names
  fieldExclusions?: string[];  // Skip specific field names
  
  // Metadata
  createdAt: string;
  updatedAt: string;
  createdBy?: string;
}

/**
 * Pre-defined filter patterns for common sensitive data types
 */
export interface FilterPattern {
  id: string;
  name: string;
  description: string;
  category: SensitiveDataCategory;
  regex: RegExp;
  testCases: string[];         // Sample data for testing
  falsePositives?: string[];   // Known false positives to avoid
}

/**
 * Filter rule set containing multiple related rules
 */
export interface FilterRuleSet {
  id: string;
  name: string;
  description: string;
  version: string;
  rules: FilterRule[];
  enabled: boolean;
  environment?: string[];      // Environments where this ruleset applies
  
  // Metadata
  createdAt: string;
  updatedAt: string;
  author?: string;
}

/**
 * Filter execution context
 */
export interface FilterContext {
  fieldPath: string;           // Path to the field being filtered (e.g., "user.email")
  fieldName: string;           // Name of the field
  value: any;                  // Original value
  parentObject: any;           // Parent object containing the field
  environment: string;         // Current environment
  logLevel: string;            // Log level of the entry
  namespace: string;           // Log namespace
  
  // Performance context
  startTime: number;           // Filter start time
  timeout: number;             // Max execution time
}

/**
 * Filter match result
 */
export interface FilterMatch {
  ruleId: string;
  ruleName: string;
  category: SensitiveDataCategory;
  severity: FilterSeverity;
  action: FilterAction;
  originalValue: any;
  filteredValue: any;
  matchedPattern: string;
  fieldPath: string;
  confidence: number;          // Confidence score (0-1)
  
  // Performance metrics
  executionTime: number;       // Time taken to apply filter (ms)
  
  // Additional context
  metadata?: Record<string, any>;
}

/**
 * Filter execution result
 */
export interface FilterResult {
  success: boolean;
  originalData: Record<string, any>;
  filteredData: Record<string, any>;
  matches: FilterMatch[];
  errors: FilterError[];
  
  // Performance metrics
  totalExecutionTime: number;
  rulesApplied: number;
  fieldsProcessed: number;
  
  // Statistics
  stats: {
    redacted: number;
    masked: number;
    removed: number;
    hashed: number;
    tokenized: number;
  };
}

/**
 * Filter execution error
 */
export interface FilterError {
  ruleId: string;
  ruleName: string;
  fieldPath: string;
  error: string;
  stack?: string;
  executionTime: number;
  timestamp: string;
}

/**
 * Filter engine configuration
 */
export interface FilterEngineConfig {
  enabled: boolean;
  
  // Performance settings
  maxExecutionTime: number;    // Max time for entire filtering operation (ms)
  maxRuleExecutionTime: number; // Max time per rule (ms)
  enableCaching: boolean;      // Enable result caching
  cacheMaxSize: number;        // Max cache entries
  cacheTTL: number;            // Cache time-to-live (ms)
  
  // Parallel processing
  enableParallelProcessing: boolean;
  maxConcurrentRules: number;
  
  // Error handling
  failSafe: boolean;           // Continue on rule errors
  logFilterErrors: boolean;    // Log filter execution errors
  
  // Rule management
  rulesets: string[];          // Active ruleset IDs
  customRules: FilterRule[];   // Additional custom rules
  
  // Environment settings
  environmentOverrides: Record<string, Partial<FilterEngineConfig>>;
  
  // Monitoring
  enableMetrics: boolean;      // Enable performance metrics
  enableAuditLog: boolean;     // Log all filter actions
  
  // Default actions
  defaultAction: FilterAction;
  defaultSeverity: FilterSeverity;
}

/**
 * Filter engine statistics
 */
export interface FilterEngineStats {
  totalFiltersApplied: number;
  totalMatchesFound: number;
  totalErrorsEncountered: number;
  averageExecutionTime: number;
  cacheHitRate: number;
  
  // Performance breakdown
  performanceBreakdown: {
    ruleExecution: number;
    resultCaching: number;
    dataProcessing: number;
    other: number;
  };
  
  // Rule statistics
  ruleStats: Record<string, {
    applications: number;
    matches: number;
    errors: number;
    averageExecutionTime: number;
  }>;
  
  // Category statistics
  categoryStats: Record<SensitiveDataCategory, {
    matches: number;
    applications: number;
  }>;
}

/**
 * Filter rule builder interface for creating rules programmatically
 */
export interface FilterRuleBuilder {
  setId(id: string): FilterRuleBuilder;
  setName(name: string): FilterRuleBuilder;
  setDescription(description: string): FilterRuleBuilder;
  setCategory(category: SensitiveDataCategory): FilterRuleBuilder;
  setType(type: FilterRuleType): FilterRuleBuilder;
  setPattern(pattern: string | RegExp): FilterRuleBuilder;
  setAction(action: FilterAction): FilterRuleBuilder;
  setPriority(priority: FilterPriority): FilterRuleBuilder;
  setSeverity(severity: FilterSeverity): FilterRuleBuilder;
  setEnabled(enabled: boolean): FilterRuleBuilder;
  addTag(tag: string): FilterRuleBuilder;
  setReplacement(replacement: string): FilterRuleBuilder;
  setPreserveFormat(preserve: boolean): FilterRuleBuilder;
  setCaseSensitive(sensitive: boolean): FilterRuleBuilder;
  setWholeWordOnly(wholeWord: boolean): FilterRuleBuilder;
  setMinLength(length: number): FilterRuleBuilder;
  setMaxLength(length: number): FilterRuleBuilder;
  setTimeout(timeout: number): FilterRuleBuilder;
  setCacheResults(cache: boolean): FilterRuleBuilder;
  setFieldNames(names: string[]): FilterRuleBuilder;
  setFieldExclusions(exclusions: string[]): FilterRuleBuilder;
  setCreatedBy(author: string): FilterRuleBuilder;
  build(): FilterRule;
}

/**
 * Filter engine interface
 */
export interface IFilterEngine {
  // Configuration
  configure(config: Partial<FilterEngineConfig>): void;
  getConfig(): FilterEngineConfig;
  
  // Rule management
  addRule(rule: FilterRule): void;
  removeRule(ruleId: string): void;
  updateRule(ruleId: string, updates: Partial<FilterRule>): void;
  getRule(ruleId: string): FilterRule | undefined;
  getRules(): FilterRule[];
  
  // Ruleset management
  addRuleSet(ruleset: FilterRuleSet): void;
  removeRuleSet(rulesetId: string): void;
  getRuleSet(rulesetId: string): FilterRuleSet | undefined;
  getRuleSets(): FilterRuleSet[];
  
  // Filtering operations
  filter(data: Record<string, any>, context?: Partial<FilterContext>): FilterResult;
  filterValue(value: any, fieldPath: string, context?: Partial<FilterContext>): FilterMatch[];
  
  // Testing and validation
  testRule(rule: FilterRule, testData: any): FilterMatch[];
  validateRule(rule: FilterRule): { valid: boolean; errors: string[] };
  
  // Performance and monitoring
  getStats(): FilterEngineStats;
  resetStats(): void;
  clearCache(): void;
  
  // Rule building
  createRuleBuilder(): FilterRuleBuilder;
}

/**
 * Cache entry for filter results
 */
export interface FilterCacheEntry {
  key: string;
  result: FilterMatch[];
  timestamp: number;
  hitCount: number;
  ruleVersion: string;
}

/**
 * Filter audit log entry
 */
export interface FilterAuditEntry {
  timestamp: string;
  ruleId: string;
  action: FilterAction;
  fieldPath: string;
  originalValue: string;
  filteredValue: string;
  confidence: number;
  executionTime: number;
  environment: string;
  logLevel: string;
  namespace: string;
} 